import { splatLoadingAtom } from '@/atoms';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useAtomValue } from 'jotai';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

/**
 * Bottom-left badge stack that surfaces splat-layer load progress while
 * `SplatMeshNode` is fetching/decoding. One row per layer; rows
 * disappear as soon as `SplatMesh.initialized` resolves. Failed loads
 * stick around so the user can see why nothing rendered.
 *
 * Sits as a sibling overlay inside the viewer's `relative` flex column
 * (see `ViewerPage.tsx`), with `pointer-events-none` so it never steals
 * interaction from the canvas.
 */
export const SplatLoadingOverlay = () => {
  const { t } = useTranslation('project');
  const loading = useAtomValue(splatLoadingAtom);
  const entries = Object.values(loading);
  if (entries.length === 0) return null;

  return (
    // Top-center, well above any side panel, with a high z-index so the
    // badge can't be hidden behind the layers tree, the toolbar, or a
    // tooltip on the left edge.
    <div className='pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-1.5'>
      {entries.map((e) => {
        const failed = e.phase === 'failed';
        const decoding = e.phase === 'decoding';
        const fetching = e.phase === 'fetching';
        const pct = typeof e.progress === 'number' ? Math.round(e.progress * 100) : null;
        // Show a determinate progress bar when we have a real percent
        // (fetching with Content-Length); fall back to an indeterminate
        // pulsing bar during decode and during chunked fetches that
        // hide their length.
        const showDeterminate = fetching && pct !== null;
        const showIndeterminate = !failed && !showDeterminate;

        return (
          <div
            key={e.layerId}
            className={cn(
              'min-w-[280px] max-w-[420px] rounded-md border bg-white/90 dark:bg-stone-900/90 backdrop-blur-md',
              'px-3 py-2 shadow-md text-xs',
              failed
                ? 'border-red-300 dark:border-red-900/60'
                : 'border-slate-200 dark:border-stone-700',
            )}>
            <div className='flex items-center gap-2'>
              {failed ? (
                <AlertTriangle className='size-3.5 shrink-0 text-red-500' />
              ) : (
                <Loader2 className='size-3.5 shrink-0 animate-spin text-blue-500' />
              )}
              <span className='flex-1 truncate font-medium'>{e.layerName}</span>
              {fetching && pct !== null && <span className='tabular-nums text-slate-500'>{pct}%</span>}
            </div>

            <div className='mt-1.5 text-[11px] text-slate-500 dark:text-slate-400'>
              {failed
                ? e.error ?? t('splatLoader.failed')
                : decoding
                  ? t('splatLoader.decoding')
                  : e.totalBytes
                    ? `${formatBytes(e.loadedBytes ?? 0)} / ${formatBytes(e.totalBytes)}`
                    : e.loadedBytes
                      ? formatBytes(e.loadedBytes)
                      : t('splatLoader.fetching')}
            </div>

            {showDeterminate && <Progress value={pct ?? 0} className='mt-1.5 h-1' />}
            {showIndeterminate && (
              <div className='mt-1.5 h-1 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-stone-800'>
                <div className='h-full w-1/3 animate-[splatpulse_1.4s_ease-in-out_infinite] rounded-full bg-blue-500' />
              </div>
            )}
          </div>
        );
      })}
      <style>{`@keyframes splatpulse { 0%{transform:translateX(-100%)} 100%{transform:translateX(300%)} }`}</style>
    </div>
  );
};
